import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { LibraryCard } from '@/lib/library/libraryCard';
import type { LibrarySpace } from '@/lib/library/collections';
import { SourceGlyph } from './SourceGlyph';
import { SPACE_SPRING, countLabel } from './spaceMotion';
import { cn } from '@/lib/utils';

/** A picture that fades in once it has decoded. Instant under reduced motion (CSS). */
function CoverImage({ src, fadeIn = true }: { src: string; fadeIn?: boolean }) {
  const ref = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(!fadeIn);
  useEffect(() => {
    // Cached images can finish before React attaches onLoad
    if (ref.current?.complete && ref.current.naturalWidth > 0) setLoaded(true);
  }, [src]);
  return (
    <img
      ref={ref}
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      onLoad={() => setLoaded(true)}
      className={cn(
        'absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ease-out motion-reduce:transition-none',
        loaded ? 'opacity-100' : 'opacity-0',
      )}
    />
  );
}

/**
 * The face of one card in a pile: its picture, or for text-only items the
 * text on raised paper with the source mark. Also used by the open
 * transition, so the flying cover matches the pile exactly.
 */
export function CoverFace({ card, size = 'md', fadeIn = true }: { card: LibraryCard; size?: 'sm' | 'md'; fadeIn?: boolean }) {
  if (card.thumbnail) {
    return (
      <div className="absolute inset-0 bg-paper-sunken">
        <CoverImage src={card.thumbnail} fadeIn={fadeIn} />
      </div>
    );
  }
  return (
    <div className="absolute inset-0 overflow-hidden bg-paper-raised">
      <SourceGlyph source={card.source} className={cn('absolute text-ink', size === 'sm' ? 'left-2 top-2 h-3 w-3' : 'left-2.5 top-2.5 h-3.5 w-3.5')} />
      <p
        className={cn(
          'whitespace-pre-line [overflow-wrap:anywhere] text-left text-ink-2',
          size === 'sm' ? 'px-2 pt-6 text-[8px] leading-[11px] line-clamp-[7]' : 'px-2.5 pt-8 text-[10.5px] leading-[14.5px] line-clamp-[7]',
        )}
      >
        {card.text || card.title}
      </p>
    </div>
  );
}

/** Back cards: where they sit and how far they turn, as a share of the card. */
const BACK = [
  { x: -17, y: 3, rotate: -7 },
  { x: 16, y: -1, rotate: 6 },
];

/**
 * A space's cover after mymind's All Spaces: up to three of its cards in a
 * small pile. The newest picture sits in front with the only shadow; two
 * more peek out behind it, turned a few degrees. Square box, card at 4:5,
 * which holds up from 150px to 200px wide (a 390px phone gives ~170px).
 */
export function SpaceCover({ space, size = 'md', className }: { space: LibrarySpace; size?: 'sm' | 'md'; className?: string }) {
  const [front, ...rest] = space.covers;
  const back = rest.slice(0, 2);
  const radius = size === 'sm' ? 'rounded-[9px]' : 'rounded-[12px]';
  return (
    <div className={cn('relative aspect-square w-full', className)} aria-hidden>
      {/* Drawn back to front: the right-hand card, then the left, then the front */}
      {back
        .map((card, i) => ({ card, pose: BACK[i] }))
        .reverse()
        .map(({ card, pose }) => (
          <div
            key={card.id}
            className={cn('absolute bottom-[6%] left-[17%] aspect-[4/5] w-[66%] overflow-hidden border border-line', radius)}
            style={{ transform: `translate(${pose.x}%, ${pose.y}%) rotate(${pose.rotate}deg)` }}
          >
            <CoverFace card={card} size={size} />
          </div>
        ))}
      {front ? (
        <div data-cover-front className={cn('absolute bottom-[6%] left-[17%] aspect-[4/5] w-[66%] overflow-hidden border border-line shadow-md', radius)}>
          <CoverFace card={front} size={size} />
        </div>
      ) : (
        <div data-cover-front className={cn('absolute bottom-[6%] left-[17%] aspect-[4/5] w-[66%] border border-dashed border-line bg-paper-sunken', radius)} />
      )}
    </div>
  );
}

/**
 * A pressable space: the pile, the name in the display face, the count.
 * Pressing scales it to 0.97 and springs back. `onOpen` gets the front
 * card's element so the open transition can grow out of it.
 */
export function SpaceTile({
  space,
  size = 'md',
  onOpen,
  className,
}: {
  space: LibrarySpace;
  size?: 'sm' | 'md';
  onOpen: (space: LibrarySpace, front: HTMLElement | null) => void;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const small = size === 'sm';
  return (
    <motion.button
      type="button"
      data-space-id={space.id}
      aria-label={`${space.label}, ${countLabel(space.cards.length)}`}
      onClick={(e) => onOpen(space, e.currentTarget.querySelector<HTMLElement>('[data-cover-front]'))}
      whileTap={reduce ? undefined : { scale: 0.97 }}
      transition={SPACE_SPRING}
      className={cn('block min-w-0 rounded-[14px] text-center outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2 focus-visible:ring-offset-paper', className)}
    >
      <SpaceCover space={space} size={size} />
      <p
        className={cn(
          'flex items-center justify-center gap-1.5 font-display font-medium tracking-[-0.01em] text-ink',
          small ? 'mt-1.5 text-[13px] leading-[17px]' : 'mt-2.5 text-[16px] leading-[21px]',
        )}
      >
        {space.source && !small && <SourceGlyph source={space.source} className="h-3.5 w-3.5 text-ink-3" />}
        <span className="truncate">{space.label}</span>
      </p>
      <p className={cn('text-ink-3', small ? 'text-[11px] leading-[15px]' : 'mt-0.5 text-[13px] leading-[18px]')}>{countLabel(space.cards.length)}</p>
    </motion.button>
  );
}
